/**
 * Realtor Listings — owner list page.
 *
 * Shows the realtor's listings (drafts + published + sold + withdrawn),
 * each with a status badge, headline, locality, and price.
 *
 * An active listing also carries an add/remove control for the public
 * portfolio. Adding publishes the listing and removing unpublishes it (the
 * same calls as the detail page), and the row is replaced with the listing
 * the server returns. Publishing is gated on verification, so a realtor who
 * is known not to be verified sees a banner and disabled add buttons; if the
 * profile cannot be loaded nothing is disabled and the server's answer is
 * what the realtor sees.
 *
 * An empty state provides a "create your first listing" nudge when
 * the realtor has zero listings. The verified gate is informational
 * here — drafting is allowed unverified, only publishing gates.
 */

import {
	AlertCircle,
	ArrowLeft,
	Plus,
	RefreshCw,
	Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ListingStatusBadge } from "@/features/realtor/components/listing-card";
import { ListingPortfolioControl } from "@/features/realtor/components/listing-portfolio-control";
import {
	type Listing,
	type ListingStatus,
	listMyListings,
	publishListing,
	unpublishListing,
} from "@/features/realtor/services/listings-api.service";
import {
	getMyRealtorProfile,
	type RealtorProfile,
} from "@/features/realtor/services/realtor-api.service";
import { deriveListingStatusBucket } from "@/features/realtor/utils/listing-status";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";
import { StudioButton } from "@/features/studio/theme";

type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "loaded"; listings: Listing[]; profile: RealtorProfile | null };

type PortfolioAction = "add" | "remove";

export function RealtorListingsListPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	// Every listing with an add/remove request in flight, so one listing's
	// request can neither disable another's button nor re-enable it.
	const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(
		() => new Set(),
	);
	const [confirmRemove, setConfirmRemove] = useState<Listing | null>(null);
	const unmountedRef = useRef(false);

	useEffect(() => {
		unmountedRef.current = false;
		return () => {
			unmountedRef.current = true;
		};
	}, []);

	// Single async loader used by both initial mount and the retry button.
	// The cancelled-flag pattern guards against setState-after-unmount when
	// the component is replaced mid-fetch (e.g. user navigates away during
	// a slow listings request).
	const runLoad = useCallback(
		async (cancelledRef: { cancelled: boolean }) => {
			setLoad({ status: "loading" });
			// The profile only decides whether "Add to portfolio" is offered, so a
			// failed profile request does not fail the page.
			const [result, profileResult] = await Promise.all([
				listMyListings(),
				getMyRealtorProfile().catch(() => null),
			]);
			if (cancelledRef.cancelled) return;
			if (!result.success || !result.data) {
				setLoad({
					status: "error",
					message: result.error ?? t("realtor.listings.loadFailedFallback"),
				});
				return;
			}
			setLoad({
				status: "loaded",
				listings: result.data,
				profile: profileResult?.success
					? (profileResult.profile ?? null)
					: null,
			});
		},
		[t],
	);

	const reload = useCallback(() => {
		runLoad({ cancelled: false });
	}, [runLoad]);

	useEffect(() => {
		const ref = { cancelled: false };
		runLoad(ref);
		return () => {
			ref.cancelled = true;
		};
	}, [runLoad]);

	const changePortfolio = async (
		listing: Listing,
		action: PortfolioAction,
	): Promise<void> => {
		const failedMessage = t(
			action === "add"
				? "realtor.listings.portfolio.addFailed"
				: "realtor.listings.portfolio.removeFailed",
		);
		setPendingIds((prev) => new Set(prev).add(listing.id));
		try {
			const result = await (action === "add"
				? publishListing(listing.id)
				: unpublishListing(listing.id));
			if (!result.success || !result.data) {
				toast.error(result.error ?? failedMessage);
				return;
			}
			if (unmountedRef.current) return;
			const updated = result.data;
			setLoad((prev) =>
				prev.status === "loaded"
					? {
							...prev,
							listings: prev.listings.map((l) =>
								l.id === updated.id ? updated : l,
							),
						}
					: prev,
			);
			toast.success(
				t(
					action === "add"
						? "realtor.listings.portfolio.addedToast"
						: "realtor.listings.portfolio.removedToast",
				),
			);
		} catch (error) {
			console.error("Changing a listing's portfolio state failed", error);
			toast.error(failedMessage);
		} finally {
			setPendingIds((prev) => {
				const next = new Set(prev);
				next.delete(listing.id);
				return next;
			});
		}
	};

	const listings = load.status === "loaded" ? load.listings : [];
	const profile = load.status === "loaded" ? load.profile : null;
	const knownUnverified =
		profile !== null && !(profile.reraVerified && profile.idVerified);
	const portfolioSlug =
		profile?.portfolioPublished && profile.publicSlug
			? profile.publicSlug
			: null;
	const onPortfolioCount = listings.filter(
		(l) => deriveListingStatusBucket(l) === "live",
	).length;

	if (load.status === "loading") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6 space-y-8">
				<div className="space-y-3" data-testid="listings-loading">
					<Skeleton className="h-12 w-full bg-dashboard-bg" />
					<Skeleton className="h-32 w-full bg-dashboard-bg" />
					<Skeleton className="h-32 w-full bg-dashboard-bg" />
				</div>
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3">
						<p className="text-sm text-dashboard-muted">{load.message}</p>
						<StudioButton
							variant="secondary"
							onClick={reload}
							className="gap-1.5 border-dashboard-border text-dashboard-text"
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.dashboard.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="bg-dashboard-surface min-h-screen p-6 space-y-8">
			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6">
					<div className="flex items-start justify-between gap-4">
						<div>
							<button
								type="button"
								onClick={() => navigate("/dashboard")}
								className="flex items-center gap-2 text-dashboard-muted hover:text-dashboard-text transition-colors mb-4"
							>
								<ArrowLeft className="h-4 w-4" />
								<span className="text-sm font-body">Back to dashboard</span>
							</button>

							<p className="text-xs font-mono uppercase tracking-widest text-dashboard-muted">
								Realtor Portal
							</p>
							<h1
								className="font-display text-3xl italic font-medium text-dashboard-text mt-2"
								style={{ lineHeight: 1.1 }}
							>
								Your{" "}
								<span className="text-dashboard-accent-display">Listings</span>
							</h1>
							<p className="text-sm text-dashboard-muted mt-2">
								{t("realtor.listings.portfolio.summary", {
									onPortfolio: onPortfolioCount,
									total: listings.length,
								})}
							</p>
							{portfolioSlug && (
								<a
									href={`/r/${portfolioSlug}`}
									target="_blank"
									rel="noopener noreferrer"
									className="mt-1 inline-block text-sm font-medium text-dashboard-accent-dark underline-offset-4 hover:underline"
								>
									{t("realtor.listings.portfolio.viewPublic")}
								</a>
							)}
						</div>
						{listings.length > 0 && (
							<StudioButton
								onClick={() => navigate("/realtor/listings/new")}
								className="gap-1.5 shrink-0"
							>
								<Plus className="h-4 w-4" />
								{t("realtor.listings.newCta")}
							</StudioButton>
						)}
					</div>
				</CardContent>
			</Card>

			{listings.length === 0 ? (
				<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-8 text-center space-y-3">
						<div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-(--color-dashboard-pill-active-bg">
							<Sparkles className="h-5 w-5 text-dashboard-accent-dark" />
						</div>
						<div className="space-y-1">
							<h2 className="font-display text-lg italic font-medium text-dashboard-text">
								{t("realtor.listings.emptyTitle")}
							</h2>
							<p className="text-sm text-dashboard-muted max-w-md mx-auto">
								{t("realtor.listings.emptySubtitle")}
							</p>
						</div>
						<StudioButton onClick={() => navigate("/realtor/listings/new")}>
							{t("realtor.listings.newCta")}
						</StudioButton>
					</CardContent>
				</Card>
			) : (
				<>
					{knownUnverified && (
						<PortfolioBanner
							message={t("realtor.listings.portfolio.verifyBanner")}
							ctaLabel={t("realtor.listings.detail.startVerificationCta")}
							onCta={() => navigate("/realtor/verification")}
						/>
					)}
					{profile && !knownUnverified && !portfolioSlug && (
						<PortfolioBanner
							message={t("realtor.listings.portfolio.offlineBanner")}
							ctaLabel={t("realtor.listings.portfolio.offlineCta")}
							onCta={() => navigate("/realtor/portfolio")}
						/>
					)}
					<div className="space-y-2">
						{listings.map((listing) => (
							<ListingRow
								key={listing.id}
								listing={listing}
								canAdd={!knownUnverified}
								pending={pendingIds.has(listing.id)}
								onAdd={() => changePortfolio(listing, "add")}
								onRemove={() => setConfirmRemove(listing)}
								onClick={() => navigate(`/realtor/listings/${listing.id}`)}
							/>
						))}
					</div>
				</>
			)}

			<Dialog
				open={confirmRemove !== null}
				onOpenChange={(open) => {
					if (!open) setConfirmRemove(null);
				}}
			>
				<DialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
					<DialogHeader>
						<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
							{t("realtor.listings.portfolio.removeConfirmTitle")}
						</DialogTitle>
						<DialogDescription className="text-sm text-dashboard-muted">
							{t("realtor.listings.portfolio.removeConfirmBody", {
								headline: confirmRemove?.headline ?? "",
							})}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<StudioButton
							variant="secondary"
							onClick={() => setConfirmRemove(null)}
						>
							{t("realtor.listings.detail.cancel")}
						</StudioButton>
						<StudioButton
							onClick={() => {
								const listing = confirmRemove;
								setConfirmRemove(null);
								if (listing) changePortfolio(listing, "remove");
							}}
						>
							{t("realtor.listings.portfolio.removeConfirmAction")}
						</StudioButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

function PortfolioBanner({
	message,
	ctaLabel,
	onCta,
}: {
	message: string;
	ctaLabel: string;
	onCta: () => void;
}) {
	return (
		<Card
			style={{
				borderColor: "var(--color-dashboard-pill-awaiting-bg)",
				backgroundColor: "var(--color-dashboard-pill-awaiting-bg)",
			}}
		>
			<CardContent className="p-4 flex items-start gap-3">
				<AlertCircle
					className="h-4 w-4 shrink-0 mt-0.5"
					style={{ color: "var(--color-dashboard-pill-awaiting)" }}
				/>
				<div className="space-y-2">
					<p className="text-xs text-dashboard-text">{message}</p>
					<StudioButton size="sm" onClick={onCta}>
						{ctaLabel}
					</StudioButton>
				</div>
			</CardContent>
		</Card>
	);
}

interface ListingRowProps {
	listing: Listing;
	canAdd: boolean;
	pending: boolean;
	onAdd: () => void;
	onRemove: () => void;
	onClick: () => void;
}

function ListingRow({
	listing,
	canAdd,
	pending,
	onAdd,
	onRemove,
	onClick,
}: ListingRowProps) {
	const { t } = useTranslation();
	const statusKey: ListingStatus = listing.status;
	return (
		<Card
			className="cursor-pointer transition hover:shadow-[var(--dashboard-shadow-3)] hover:-translate-y-0.5 bg-dashboard-bg border-dashboard-border rounded-xl"
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onClick();
				}
			}}
			role="button"
			tabIndex={0}
		>
			<CardContent className="p-5">
				<div className="flex items-start justify-between gap-4">
					<div className="min-w-0 flex-1">
						<p
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 18,
								fontWeight: 500,
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
								lineHeight: 1.2,
							}}
							className="truncate"
						>
							{listing.headline}
						</p>
						<p
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 10,
								fontWeight: 600,
								letterSpacing: "0.15em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-muted)",
								marginTop: 6,
							}}
							className="truncate"
						>
							{listing.locality} · {listing.city}
						</p>
					</div>
					<ListingStatusBadge status={statusKey} isPublic={listing.isPublic} />
				</div>
				<div
					className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 pt-3"
					style={{ borderTop: "1px solid var(--color-dashboard-border)" }}
				>
					<span
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 16,
							fontWeight: 500,
							color: "var(--color-dashboard-text)",
						}}
					>
						{formatPaiseAsRupees(listing.askingPrice)}
					</span>
					{listing.bhk != null && (
						<span
							className="text-xs"
							style={{
								color: "var(--color-dashboard-muted)",
								fontFamily: "var(--font-body)",
							}}
						>
							· {listing.bhk} BHK
						</span>
					)}
					{listing.sizeSqft != null && (
						<span
							className="text-xs"
							style={{
								color: "var(--color-dashboard-muted)",
								fontFamily: "var(--font-body)",
							}}
						>
							· {listing.sizeSqft} {t("realtor.listings.sqft")}
						</span>
					)}
				</div>
				<ListingPortfolioControl
					listing={listing}
					canAdd={canAdd}
					pending={pending}
					onAdd={onAdd}
					onRemove={onRemove}
				/>
			</CardContent>
		</Card>
	);
}
